/**
 * The progress API client code is mostly copied from the Sphere Viewer app client:
 * https://dev.azure.com/faro-connect/ViewerApp/_git/LotvMonorepo?path=/apps/sphere-web-viewer/src/apis/progress-api
 */

import { TokenProvider } from "@faro-lotv/service-wires";
import {
  BackgroundTaskType,
  ProgressApiLatestStatusResponse,
} from "@api/progress-api/progress-api-types";
import { isProgressApiLatestStatusResponse } from "@api/progress-api/progress-api-type-guards";
import { CLIENT_ID } from "@api/client-id";
import { ISOTimeString } from "@faro-lotv/ielement-types";

/** Number of task items per page that we will fetch from the ProgressAPI */
const ITEMS_PER_PAGE = 30;

type QueryParams = Pick<ProgressApiLatestStatusResponse, "before"> & {
  /** Page size per API request. Default = ITEMS_PER_PAGE. */
  pageSize?: number;
  /** Whether to load all pages from API. Default = false (load 1st page only). */
  shouldFetchAllPages?: boolean;
  /** Name of the task type to query */
  taskType?: BackgroundTaskType;
  /** Fetch only tasks which were updated on this date or later. */
  fromDate?: ISOTimeString;
};

/**
 * A class to handle the queries regarding long running background tasks
 */
export class ProgressApiClient {
  /**
   * @param projectId Id of the current project
   * @param tokenProvider To get tokens for this project
   * @param baseUrl The base url of the progress api
   */
  constructor(
    private projectId: string,
    private tokenProvider: TokenProvider,
    private baseUrl: string
  ) {}

  /**
   * @returns the state of the backend tasks
   */
  async requestProgress({
    pageSize = ITEMS_PER_PAGE,
    before,
    taskType,
    shouldFetchAllPages,
    fromDate,
  }: QueryParams): Promise<ProgressApiLatestStatusResponse> {
    const url = new URL(
      `${this.baseUrl}/v1/status/latest?projectId=${this.projectId}&pageSize=${pageSize}`
    );

    // https://progress.api.dev.holobuilder.eu/swagger/index.html#section/Pagination
    // "last: Set to true to request the last page. Useful for backwards pagination."
    // That seems not fully correct, because e.g. with pageSize=3 and 5 tasks in total,
    // we always get 3 tasks instead of 2.
    // So it's rather "request the first page, with reverse ordering = newest first,
    // then reverse the items in the page again (newest task = last item)."
    if (before) {
      url.searchParams.append("before", before);
    } else {
      url.searchParams.append("last", "true");
    }

    if (taskType) {
      url.searchParams.append("taskType", taskType);
    }

    if (fromDate) {
      url.searchParams.append("fromDate", fromDate);
    }

    const res = await fetch(url.toString(), {
      method: "GET",
      headers: {
        Authorization: `Bearer ${await this.tokenProvider()}`,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        "X-Holobuilder-Component": CLIENT_ID,
      },
    });
    if (!res.ok) {
      throw new Error(`ProgressAPI request failed: ${res.status} ${res.statusText}`);
    }

    const response = await res.json();
    // Previously, `data` was allowed to be `null`, so let's assign an empty array as fallback, to be safe.
    response.data = response.data ?? [];

    if (!isProgressApiLatestStatusResponse(response)) {
      throw new Error("ProgressAPI returned invalid response", {
        cause: response,
      });
    }

    if (shouldFetchAllPages) {
      // Call API in a loop to fetch previous pages (= less recently updated tasks).
      // This code assumes that the first page was fetched with the "last" parameter.
      // With "last"    -> Paginate using "before".
      // Without "last" -> Paginate using "after".
      for (let page = response; page.before; ) {
        page = await this.requestProgress({
          pageSize,
          before: page.before,
          shouldFetchAllPages: false,
          taskType,
          fromDate,
        });
        // The tasks on each page are ordered from oldest to newest, but the pages are ordered from newest to oldest.
        // -> The order of the final result is inconsistent, but it doesn't matter for our purposes.
        response.data.push(...page.data);
      }
      // If we fetch all tasks, the pagination tokens are not needed anymore.
      response.after = null;
      response.before = null;
    }

    return response;
  }
}
